
import React, { useState } from "react";
import { 
  Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle 
} from "@/components/ui/card";
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, 
  DialogHeader, DialogTitle, DialogTrigger 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { Tenement } from "@/lib/types";
import { MapPin, Plus, Edit, Trash, FileText } from "lucide-react";

export function TenementsManager() {
  const [tenements, setTenements] = useState<Tenement[]>([]);
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newTenement, setNewTenement] = useState<Partial<Tenement>>({
    name: "",
    location: "",
    size: 0,
    mineralType: "",
    status: "exploration",
    acquisitionDate: new Date().toISOString().split('T')[0],
    expiryDate: new Date(new Date().setFullYear(new Date().getFullYear() + 5)).toISOString().split('T')[0],
    ownershipPercentage: 100
  });

  const handleAddTenement = () => {
    const tenementToAdd: Tenement = {
      id: Date.now().toString(), // Simple ID generation for demo
      ...newTenement as Omit<Tenement, 'id'>
    };
    
    setTenements([...tenements, tenementToAdd]);
    setNewTenement({
      name: "",
      location: "",
      size: 0,
      mineralType: "",
      status: "exploration",
      acquisitionDate: new Date().toISOString().split('T')[0],
      expiryDate: new Date(new Date().setFullYear(new Date().getFullYear() + 5)).toISOString().split('T')[0],
      ownershipPercentage: 100
    });
    setIsAddDialogOpen(false);
  };

  const handleDeleteTenement = (id: string) => {
    setTenements(tenements.filter(t => t.id !== id));
  };

  const getStatusBadge = (status: Tenement['status']) => {
    switch (status) {
      case "prospecting":
        return <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300">Prospecting</Badge>;
      case "exploration":
        return <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">Exploration</Badge>;
      case "development":
        return <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">Development</Badge>;
      case "production":
        return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">Production</Badge>;
      case "dormant":
        return <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-300">Dormant</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div className="space-y-0.5">
          <h2 className="text-2xl font-bold tracking-tight">Tenements Management</h2>
          <p className="text-muted-foreground">
            Manage your mineral exploration tenements and mining leases.
          </p>
        </div>
        
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Tenement
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[525px]">
            <DialogHeader>
              <DialogTitle>Add New Tenement</DialogTitle>
              <DialogDescription>
                Enter the details of the new tenement or mining lease.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Tenement Name</Label>
                  <Input
                    id="name"
                    value={newTenement.name}
                    onChange={(e) => setNewTenement({...newTenement, name: e.target.value})}
                    placeholder="e.g. Gold Ridge"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={newTenement.location}
                    onChange={(e) => setNewTenement({...newTenement, location: e.target.value})}
                    placeholder="e.g. Western Australia"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="size">Size (hectares)</Label>
                  <Input
                    id="size"
                    type="number"
                    value={newTenement.size}
                    onChange={(e) => setNewTenement({...newTenement, size: Number(e.target.value)})}
                    placeholder="e.g. 1250"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="mineralType">Primary Mineral</Label>
                  <Select 
                    value={newTenement.mineralType}
                    onValueChange={(value) => setNewTenement({...newTenement, mineralType: value})}
                  >
                    <SelectTrigger id="mineralType">
                      <SelectValue placeholder="Select mineral" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gold">Gold</SelectItem>
                      <SelectItem value="silver">Silver</SelectItem>
                      <SelectItem value="copper">Copper</SelectItem>
                      <SelectItem value="iron">Iron</SelectItem>
                      <SelectItem value="lithium">Lithium</SelectItem>
                      <SelectItem value="rare_earth">Rare Earth Elements</SelectItem>
                      <SelectItem value="uranium">Uranium</SelectItem>
                      <SelectItem value="zinc">Zinc</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select 
                    value={newTenement.status}
                    onValueChange={(value: Tenement['status']) => setNewTenement({...newTenement, status: value})}
                  >
                    <SelectTrigger id="status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="prospecting">Prospecting</SelectItem>
                      <SelectItem value="exploration">Exploration</SelectItem>
                      <SelectItem value="development">Development</SelectItem>
                      <SelectItem value="production">Production</SelectItem>
                      <SelectItem value="dormant">Dormant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="ownershipPercentage">Ownership (%)</Label>
                  <Input
                    id="ownershipPercentage"
                    type="number"
                    min="0"
                    max="100"
                    value={newTenement.ownershipPercentage}
                    onChange={(e) => setNewTenement({...newTenement, ownershipPercentage: Number(e.target.value)})}
                    placeholder="e.g. 100"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="acquisitionDate">Acquisition Date</Label>
                  <Input
                    id="acquisitionDate"
                    type="date"
                    value={newTenement.acquisitionDate}
                    onChange={(e) => setNewTenement({...newTenement, acquisitionDate: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="expiryDate">Expiry Date</Label>
                  <Input
                    id="expiryDate"
                    type="date"
                    value={newTenement.expiryDate}
                    onChange={(e) => setNewTenement({...newTenement, expiryDate: e.target.value})}
                  />
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddTenement}>Add Tenement</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Tenements Portfolio</CardTitle>
          <CardDescription>
            Your managed mining tenements and exploration licenses
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tenements.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Mineral</TableHead>
                  <TableHead>Size (ha)</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ownership</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenements.map((tenement) => (
                  <TableRow key={tenement.id}>
                    <TableCell className="font-medium">{tenement.name}</TableCell>
                    <TableCell><MapPin className="mr-1 h-3 w-3 inline" />{tenement.location}</TableCell>
                    <TableCell className="capitalize">{tenement.mineralType.replace('_', ' ')}</TableCell>
                    <TableCell>{tenement.size}</TableCell>
                    <TableCell>{getStatusBadge(tenement.status)}</TableCell>
                    <TableCell>{tenement.ownershipPercentage}%</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="outline" size="icon" title="View Reports">
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="icon" title="Edit">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        title="Delete"
                        onClick={() => handleDeleteTenement(tenement.id)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 px-4">
              <div className="mb-4">
                <MapPin className="h-12 w-12 mx-auto text-muted-foreground opacity-30" />
              </div>
              <h3 className="text-lg font-medium mb-2">No tenements added yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Add your first tenement to start tracking mineral assets and creating financial projections.
              </p>
              <Button 
                onClick={() => setIsAddDialogOpen(true)}
                className="mx-auto"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Tenement
              </Button>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <div>
            <p className="text-sm text-muted-foreground">
              Total Tenements: {tenements.length}
            </p>
          </div>
          <Button variant="outline">Export Data</Button>
        </CardFooter>
      </Card>
    </div>
  );
}
